import React, { useMemo } from 'react';
import type { Test } from '@mariana/labos-client';
import { WorksheetViewer } from '../WorksheetViewer';
import { SpreadsheetTable } from '../SpreadsheetTable';
import './ResultViewers.css';

interface CamsizerResultViewerProps {
  test: Test;
}

/**
 * Camsizer particle-size results. The reported values are whatever
 * characteristic-value rows the instrument method exported (they differ
 * per material), so this viewer renders every report_export_* entry using
 * the original instrument label carried in the entry's `label` field.
 */
export const CamsizerResultViewer: React.FC<CamsizerResultViewerProps> = ({ test }) => {
  const worksheetData = test.attributes?.worksheet_data as Record<string, any> | undefined;
  const rawWorksheetData = test.attributes?.raw_worksheet_data as Record<string, Record<string, any>> | undefined;

  const reportedRows = useMemo(() => {
    if (!worksheetData) return [];
    return Object.entries(worksheetData)
      .filter(([key]) => key.startsWith('report_export_'))
      .map(([key, entry]) => {
        const label = entry?.label ?? key.replace(/^report_export_/, '');
        const value = entry?.value ?? entry;
        const unit = entry?.unit ?? '';
        return [String(label), String(value ?? ''), unit] as [string, string, string];
      });
  }, [worksheetData]);

  return (
    <div className="rv-container">
      {rawWorksheetData && (
        <div className="rv-section rv-section-raw">
          <h3 className="rv-section-title">Raw Worksheet</h3>
          <WorksheetViewer rawWorksheetData={rawWorksheetData} />
        </div>
      )}

      {worksheetData && reportedRows.length > 0 && (
        <div className="rv-section rv-section-reported">
          <h3 className="rv-section-title">Reported Values</h3>
          <SpreadsheetTable
            headers={['Characteristic', 'Value', 'Unit']}
            rows={reportedRows}
            showRowNumbers={false}
          />
        </div>
      )}

      {!worksheetData && !rawWorksheetData && (
        <div className="rv-empty">No Camsizer results available for this test.</div>
      )}
    </div>
  );
};
